/**
 * AuthPage — the sign-in / sign-up screen.
 *
 * Split-screen layout, design-focused for now (no Auth0 wiring yet):
 *   - Left  : the pinned MatchView brand panel (PitchBackdrop + logo + tagline).
 *             Hidden below `lg` so small screens show only the form.
 *   - Right : a minimal email/password form with a Log in / Sign up toggle.
 *
 * Built entirely on existing tokens + primitives (Button, Input, Label, the
 * brand components) so it reads as part of the app. The submit handlers are
 * stubs — they'll be pointed at Auth0 in the wiring step.
 */

import { useState } from 'react'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MatchViewLogo } from '@/components/brand/matchview-logo'
import { PitchBackdrop } from '@/components/brand/pitch-backdrop'
import { createLogger } from '@/shared/logging/logger'
import { cn } from '@/shared/ui/cn'

const logger = createLogger('AuthPage')

type Mode = 'login' | 'signup'

// Plain-English strings for now; TODO: route through i18n once the page is wired.
const COPY = {
  tagline: 'Review every match, frame by frame.',
  login: {
    heading: 'Welcome back',
    sub: 'Log in to your MatchView workspace.',
    submit: 'Log in',
    switchPrompt: "Don't have an account?",
    switchAction: 'Sign up',
  },
  signup: {
    heading: 'Create your account',
    sub: 'Start reviewing matches in minutes.',
    submit: 'Create account',
    switchPrompt: 'Already have an account?',
    switchAction: 'Log in',
  },
} as const

export function AuthPage() {
  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const copy = COPY[mode]

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    // TODO: wire to Auth0 (loginWithRedirect / signup). Stubbed for the design pass.
    setSubmitting(true)
    logger.info(`auth submit (stub): mode=${mode} email=${email}`)
    setTimeout(() => setSubmitting(false), 600)
  }

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-background px-6 py-12">
      {/* Full-screen pitch backdrop — same hero treatment as the landing page. */}
      <PitchBackdrop />

      {/* Centered column, echoing the landing page's hero composition. */}
      <div className="relative z-10 flex w-full max-w-sm flex-col items-center animate-fade-in">
        <MatchViewLogo variant="full" size="lg" className="mb-6" />

        {/* Brand hero — the landing-page line, tying the two screens together. */}
        <h1 className="mb-8 text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
          Break down the game.
          <br />
          <span className="text-primary">Frame by frame.</span>
        </h1>

        {/* The form panel — frosted glass: a mostly-opaque card with a strong
            backdrop blur, so the pitch behind it melts into a soft wash instead
            of busy lines. A faint white edge + soft shadow lift it gently. */}
        <div className="w-full rounded-2xl border border-white/10 bg-card/85 p-8 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.55)] backdrop-blur-xl">
          {/* Log in / Sign up segmented toggle */}
          <div className="mb-8 grid grid-cols-2 gap-1 rounded-lg bg-background p-1">
            {(['login', 'signup'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  'rounded-md py-1.5 text-sm font-medium transition-colors',
                  mode === m
                    ? 'bg-accent text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {m === 'login' ? 'Log in' : 'Sign up'}
              </button>
            ))}
          </div>

          {/* Per-mode heading — replaced by the brand hero above; kept (commented)
              for easy revival rather than deleted.
          <div className="mb-6">
            <h1 className="text-2xl font-semibold tracking-tight">{copy.heading}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{copy.sub}</p>
          </div>
          */}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="auth-email">Email</Label>
              <Input
                id="auth-email"
                type="email"
                autoComplete="email"
                placeholder="you@club.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="auth-password">Password</Label>
              <div className="relative">
                <Input
                  id="auth-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" size="lg" className="w-full gap-2" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {copy.submit}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {copy.switchPrompt}{' '}
            <button
              type="button"
              onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
              className="font-medium text-primary hover:underline"
            >
              {copy.switchAction}
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}
